/**
 * Validación de integridad del modal de contrato antes de avanzar etapas del pipeline.
 */

export const DOC_FILE_FIELDS = [
  'csf_url',
  'contractDocUrl',
  'fad_zip_url',
  'propuesta_url',
  'tdSynnexContractUrl'
];

export function hasLicencias(contract) {
  return Array.isArray(contract?.licencias) && contract.licencias.length > 0;
}

export const LEGAL_STATUS_NOT_REQUIRED = 'NOT_REQUIRED';
export const LEGAL_SKIP_REASON_GEMINI_EXISTING = 'EXISTING_CLIENT_GEMINI_ENTERPRISE_ONLY';

function licenseGateText(licOrSku) {
  if (licOrSku && typeof licOrSku === 'object') {
    return [licOrSku.sku, licOrSku.skuLabel, licOrSku.description, licOrSku.name, licOrSku.descripcion]
      .filter(Boolean)
      .join(' ');
  }
  return String(licOrSku || '');
}

function isPcSatSku(sku) {
  return /^\d{5,}$/.test(String(sku || '').trim());
}

function quotedLicenciasFromProposal(proposal) {
  if (!proposal) return [];
  const opciones = proposal.opciones;
  if (Array.isArray(opciones) && opciones.length > 0) {
    const active = opciones.find((o) => o.isRecommended) || opciones[0];
    if (active?.licencias?.length) return active.licencias;
    for (const op of opciones) {
      if (Array.isArray(op?.licencias) && op.licencias.length > 0) return op.licencias;
    }
  }
  if (Array.isArray(proposal.licencias) && proposal.licencias.length > 0) return proposal.licencias;
  return [];
}

function licenseQty(lic) {
  return Number(lic?.qty) || Number(lic?.seats) || Number(lic?.cantidad) || 0;
}

/** SKU Gemini Enterprise (Standard / Plus / Business). No incluye Workspace «con Gemini». */
export function isGeminiEnterpriseSku(licOrSku) {
  const n = licenseGateText(licOrSku).toLowerCase().normalize('NFD').replace(/\p{M}/gu, '');
  if (!n.includes('gemini') || !n.includes('enterprise')) return false;
  if (n.includes('google workspace')) return false;
  if (n.includes('workspace business') || n.includes('workspace enterprise')) return false;
  return true;
}

/** Standard / Plus se firman con TD Synnex; Business va directo con Google. */
function isGeminiTdSynnexSku(licOrSku) {
  if (!isGeminiEnterpriseSku(licOrSku)) return false;
  const n = licenseGateText(licOrSku).toLowerCase().normalize('NFD').replace(/\p{M}/gu, '');
  if (n.includes('edicion business') || n.includes('edition business')) return false;
  if (n.includes('frontline') || n.includes('mercados emergentes')) return false;
  return n.includes('standard') || n.includes('plus') || n.includes('edicion standard') || n.includes('edicion plus');
}

/**
 * Cotización comercial (propuesta CRM si existe; si no, licencias del expediente).
 * Ignora inventario SAT/Por Cobrar cuando hay propuesta de catálogo.
 */
export function resolveQuotedLicenciasForLegalGate(contract = {}) {
  const fromProposal = quotedLicenciasFromProposal(contract.proposal);
  const stored = Array.isArray(contract.licencias) ? contract.licencias : [];
  const isCrmQuote = fromProposal.length > 0 && contract.proposal?.source !== 'operational';
  if (isCrmQuote) return fromProposal;
  const storedOnlyPc = stored.length > 0 && stored.every((l) => isPcSatSku(l.sku));
  const proposalHasCatalog = fromProposal.some((l) => !isPcSatSku(l.sku));
  if (storedOnlyPc && proposalHasCatalog) return fromProposal;
  if (stored.length > 0) return stored;
  return fromProposal;
}

/** La venta actual es únicamente Gemini Enterprise (qty > 0). */
export function isGeminiEnterpriseOnlyQuote(contract = {}) {
  const items = resolveQuotedLicenciasForLegalGate(contract).filter((l) => licenseQty(l) > 0);
  if (!items.length) return false;
  return items.every(isGeminiEnterpriseSku);
}

/** Ya opera con Plexarian (Por Cobrar / GWS / historial post-firma). */
export function isExistingOperationalCustomer(contract = {}) {
  if (!contract) return false;
  if (contract.is_synced_with_pc === true) return true;
  if (contract.por_cobrar_uuid || contract.customerId) return true;
  if ((Number(contract.numero_licencias_activas) || 0) > 0) return true;
  const origin = String(contract.clientOrigin || '');
  if (origin === 'por_cobrar' || origin === 'por_cobrar_operational') return true;
  const hist = contract.pipelineHistory || [];
  if (hist.some((e) => ['IMPLEMENTATION', 'BILLING', 'CLOSED', 'VERIFIED'].includes(e?.stage))) {
    return true;
  }
  return (contract.history || []).some((h) => h?.action === 'MOVE_TO_COMMERCIAL_REVIEW');
}

/**
 * Cliente existente que cotiza SOLO Gemini Enterprise: no requiere contrato/Legal.
 * Un alta nueva (aunque sea solo Gemini) sí pasa por Legal.
 */
export function canSkipLegalToImplementation(contract = {}) {
  return isExistingOperationalCustomer(contract) && isGeminiEnterpriseOnlyQuote(contract);
}

export function proposalOptionHasCommercialLines(opt) {
  return Boolean(
    (Array.isArray(opt?.licencias) && opt.licencias.length > 0)
    || (Array.isArray(opt?.serviciosAdicionales) && opt.serviciosAdicionales.length > 0)
  );
}

/** Opción hueca (título sin SKUs): rellena con licencias/servicios del contrato. */
export function hydrateProposalOpcionesFromContract(opciones = [], contract = {}) {
  const list = Array.isArray(opciones) ? opciones : [];
  const fallbackLic = Array.isArray(contract?.licencias) ? contract.licencias : [];
  const fallbackServ = Array.isArray(contract?.serviciosAdicionales) ? contract.serviciosAdicionales : [];
  if (list.length === 0) return [];
  return list.map((opt) => {
    if (proposalOptionHasCommercialLines(opt)) return opt;
    if (!fallbackLic.length && !fallbackServ.length) return opt;
    return {
      ...opt,
      ...(fallbackLic.length ? { licencias: fallbackLic } : {}),
      ...(fallbackServ.length ? { serviciosAdicionales: fallbackServ } : {}),
      vigenciaLabel: opt.vigenciaLabel || contract.anexo?.vigenciaLabel || contract.proposal?.vigenciaLabel,
      billingFrequency: opt.billingFrequency
        || contract.anexo?.billingFrequency
        || contract.billingFrequency
        || contract.proposal?.billingFrequency
    };
  });
}

export function hasValidCommercialProposal(proposal) {
  if (!proposal) return false;
  if (Array.isArray(proposal.licencias) && proposal.licencias.length > 0) return true;
  if (Array.isArray(proposal.serviciosAdicionales) && proposal.serviciosAdicionales.length > 0) return true;
  if (Array.isArray(proposal.opciones) && proposal.opciones.some(proposalOptionHasCommercialLines)) return true;
  return false;
}

/**
 * Nunca sustituye una propuesta con líneas por un escenario hueco.
 * Si la nueva opción no trae SKUs, conserva las del expediente.
 */
export function mergePreserveProposalLines(existingProposal, incomingProposal, fallbackLicencias = []) {
  if (!incomingProposal) return existingProposal || null;
  if (!hasValidCommercialProposal(incomingProposal) && hasValidCommercialProposal(existingProposal)) {
    return existingProposal;
  }
  const incomingOps = Array.isArray(incomingProposal.opciones) ? incomingProposal.opciones : [];
  if (incomingOps.length === 0) return incomingProposal;
  const existingOps = Array.isArray(existingProposal?.opciones) ? existingProposal.opciones : [];
  const existingByKey = new Map(existingOps.map((o, i) => [String(o.id ?? o.titulo ?? i), o]));
  const fallback = (Array.isArray(fallbackLicencias) && fallbackLicencias.length)
    ? fallbackLicencias
    : (existingProposal?.licencias || []);

  const opciones = incomingOps.map((opt, i) => {
    if (proposalOptionHasCommercialLines(opt)) return opt;
    const prev = existingByKey.get(String(opt.id ?? opt.titulo ?? i)) || existingOps[i];
    if (proposalOptionHasCommercialLines(prev)) {
      return {
        ...opt,
        licencias: prev.licencias,
        serviciosAdicionales: prev.serviciosAdicionales || opt.serviciosAdicionales
      };
    }
    if (fallback.length) return { ...opt, licencias: fallback };
    return opt;
  });
  return { ...incomingProposal, opciones };
}

/**
 * Propuesta comercial lista para avanzar a Legal:
 * - Comercial ya aprobó (salesStatus APPROVED), o
 * - Hay propuesta válida y ya fue enviada al cliente.
 */
export function isCommercialProposalComplete(contract = {}) {
  if (contract.salesStatus === 'APPROVED') return true;
  const proposal = contract.proposal || {};
  if (!hasValidCommercialProposal(proposal) && !hasLicencias(contract)) return false;
  const sentAt = proposal.sentAt || contract['proposal.sentAt'] || contract.lastEmailDelivery?.at;
  if (sentAt) return true;
  if (Number(contract.anexo?.totalMensual) > 0 || Number(proposal.totalMensual) > 0) return true;
  return false;
}

/** Onboarding + propuesta comercial listos → puede ir a Legal. */
export function canAdvanceOnboardingToLegal(contract = {}) {
  if (canSkipLegalToImplementation(contract)) return false;
  const onboardingDone = contract.onboardingCrmStatus === 'COMPLETADO'
    || contract.renewal_onboarding_completed === true
    || Boolean(contract.csf_url || contract.id_url || contract.acta_url);
  return onboardingDone && isCommercialProposalComplete(contract);
}

export function hasCommercialSummary(contract) {
  return (
    hasValidCommercialProposal(contract?.proposal)
    || hasLicencias(contract)
    || Boolean(contract?.propuesta_url)
    || Boolean(contract?.shortId)
  );
}

export function hasLegalExpediente(contract) {
  return Boolean(contract?.csf_url || contract?.contractDocUrl || contract?.fad_zip_url);
}

export function hasContact(contract) {
  return Boolean(resolveContractAdminContact(contract).email);
}

/** Contacto admin del cliente (misma lógica que el backend). */
export function resolveContractAdminContact(contract = {}) {
  const contactos = contract.contactos || {};
  const admin = contactos.admin || contactos.pagos || {};
  const legacyArray = Array.isArray(contactos) ? contactos[0] : null;

  const email = String(
    admin.email
    || contactos.contacto1?.correo
    || legacyArray?.email
    || contract.contactEmail
    || contract.clientEmail
    || contract.email
    || ''
  ).trim().toLowerCase();

  const name = (
    [admin.nombre, admin.apellido].filter(Boolean).join(' ').trim()
    || legacyArray?.name
    || contactos.contacto1?.nombre
    || contract.representante_legal
    || 'Cliente'
  );

  const phone = String(
    admin.telefono || admin.phone || legacyArray?.phone || contract.telefono || ''
  ).trim();

  return { email, name, phone };
}

export function isPorCobrarOperational(contract) {
  return (
    Boolean(contract?.por_cobrar_uuid)
    && (contract?.clientOrigin === 'por_cobrar' || contract?.clientOrigin === 'por_cobrar_operational' || contract?.is_shadow)
  );
}

/**
 * @param {object} contract
 * @param {string} targetStage
 * @returns {{ canAdvance: boolean, message?: string, warnings?: string[] }}
 */
export function assessContractIntegrity(contract, targetStage) {
  const warnings = [];
  const name = contract?.razon_social || contract?.nombre_comercial || contract?.companyName;
  const domain = contract?.dominio || contract?.domain;

  if (!domain) {
    return { canAdvance: false, message: 'El contrato debe tener dominio antes de avanzar de etapa.' };
  }

  if (!name) {
    return { canAdvance: false, message: 'Falta razón social o nombre comercial.' };
  }

  if (['LEGAL_REVIEW', 'SIGNING', 'IMPLEMENTATION', 'BILLING', 'VERIFIED', 'CLOSED'].includes(targetStage)) {
    if (!hasCommercialSummary(contract)) {
      return {
        canAdvance: false,
        message:
          'Falta propuesta o licencias. Genera la propuesta en CRM o completa el desglose comercial antes de continuar.'
      };
    }
    if (!hasContact(contract)) {
      warnings.push('Sin correo de contacto registrado.');
    }
  }

  if (['IMPLEMENTATION', 'BILLING', 'VERIFIED', 'CLOSED'].includes(targetStage)) {
    const skipLegal = canSkipLegalToImplementation(contract);
    if (!hasLegalExpediente(contract) && !skipLegal) {
      return {
        canAdvance: false,
        message:
          'Falta expediente legal (CSF, contrato o ZIP FAD). Completa onboarding o sube documentos antes de implementación/cierre.'
      };
    }
    const quoted = resolveQuotedLicenciasForLegalGate(contract).filter((l) => licenseQty(l) > 0);
    if (quoted.some(isGeminiTdSynnexSku) && !contract?.tdSynnexContractUrl && !contract?.tdSynnexContractZipUrl) {
      warnings.push('Falta la carta de aceptación firmada con TD Synnex (Gemini Enterprise Standard/Plus).');
    }
  }

  if (targetStage === 'CLOSED' && !hasLegalExpediente(contract)
      && contract.legalStatus !== LEGAL_STATUS_NOT_REQUIRED
      && !canSkipLegalToImplementation(contract)) {
    return {
      canAdvance: false,
      message: 'No se puede cerrar sin expediente legal vinculado (CSF o contrato firmado).'
    };
  }

  return { canAdvance: true, warnings };
}

/** Campos de documentos que deben replicarse a todos los registros del dominio. */
export const DOMAIN_DOC_SYNC_KEYS = [
  'csf_url',
  'id_url',
  'acta_url',
  'contractDocUrl',
  'fad_zip_url',
  'tdSynnexContractUrl',
  'tdSynnexContractZipUrl',
  'tdSynnexContractFileName',
  'tdSynnexContractUploadedAt',
  'propuesta_url',
  'lista_usuarios_url',
  'userListSheetUrl',
  'driveFolderId',
  'proposal',
  'licencias',
  'anexo',
  'contactos',
  'crmLeadId',
  'shortId',
  'proposalMicrositeToken',
  'proposalMicrositePath',
  'por_cobrar_customer_url'
];

export function pickDomainSyncPatch(updates = {}) {
  const patch = {};
  for (const key of DOMAIN_DOC_SYNC_KEYS) {
    if (updates[key] !== undefined && updates[key] !== null) {
      patch[key] = updates[key];
    }
  }
  return patch;
}
